import { memo, useCallback, useMemo } from 'react'
import { SxProps } from '@mui/material'
import { useTranslations } from '@/core/translation'
import { Slider, Text } from '@/components/Data'
import { Box } from '@/components/Layout'
import { timeRangeSliderStyles as styles } from './TimeRangeSlider.style'
import { TimeRangeSliderProps } from './TimeRangeSlider.types'
import {
  TIME_RANGE_SLIDER_DEFAULT_MAX,
  TIME_RANGE_SLIDER_DEFAULT_MIN,
} from './TimeRangeSlider.data'
import { formatTimeRangeSliderLabel } from './TimeRangeSlider.utils'
import { useTimeRangeSlideUtils } from './TimeRangeSlider.hooks'

function TimeRangeSlider({
  showTitle = true,
  min = TIME_RANGE_SLIDER_DEFAULT_MIN,
  max = TIME_RANGE_SLIDER_DEFAULT_MAX,
  value,
  onChange,
  wrapperProps = {},
  titleTextProps = {},
  title,
  valueLabelProps = {},
  durationLabel,
  sliderProps = {},
}: TimeRangeSliderProps) {
  const t = useTranslations('components')
  const { getDurationValueText } = useTimeRangeSlideUtils()

  const onChangeWrapper = useCallback(
    (_: Event, newValue: number | number[]) => {
      if (!Array.isArray(newValue)) return
      const [a, b] = newValue
      const from = Math.min(a, b)
      const to = Math.max(a, b)
      onChange({ min: from, max: to })
    },
    [onChange],
  )

  const { sx: wrapperSx, ...otherWrapperProps } = wrapperProps
  const { sx: titleSx, ...otherTitleProps } = titleTextProps
  const { sx: durationLabelSx, ...otherDurationLabelProps } = valueLabelProps

  const durationValueText = useMemo(
    () => getDurationValueText({ value, min, max }),
    [getDurationValueText, max, min, value],
  )

  return (
    <Box sx={[styles.wrapper, wrapperSx] as SxProps} {...otherWrapperProps}>
      {showTitle && (
        <Text sx={[styles.title, titleSx] as SxProps} {...otherTitleProps}>
          {title ?? t('timeRangeSlider.title')}
        </Text>
      )}

      <Text sx={[styles.valueLabel, durationLabelSx] as SxProps} {...otherDurationLabelProps}>
        {durationLabel ?? t('timeRangeSlider.duration')} {durationValueText}
      </Text>

      <Slider
        value={[value.min || min, value.max || max]}
        min={min}
        max={max}
        onChange={onChangeWrapper}
        valueLabelFormat={formatTimeRangeSliderLabel}
        {...sliderProps}
      />
    </Box>
  )
}

export default memo(TimeRangeSlider)
